
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Select,
  Divider,
  Box,
  Badge
} from '@chakra-ui/react';

import { CategorySelector } from './CategorySelector';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  onSave: (updates: { pfc_primary?: string; pfc_detailed?: string; merchant?: string; normalized_merchant?: string }) => Promise<void>;
  availableCategories: { primary: string; detailed: string; }[];
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({ isOpen, onClose, selectedCount, onSave, availableCategories }) => {
  const [merchant, setMerchant] = useState('');
  const [normalizedMerchant, setNormalizedMerchant] = useState('');
  const [pfcPrimary, setPfcPrimary] = useState('');
  const [pfcDetailed, setPfcDetailed] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset state on open
  React.useEffect(() => {
      if(isOpen) {
          setMerchant('');
          setNormalizedMerchant('');
          setPfcPrimary('');
          setPfcDetailed('');
      }
  }, [isOpen]);

  const handleSave = async () => {
    if (!merchant && !normalizedMerchant && !pfcPrimary && !pfcDetailed) {
        // Nothing to update
        onClose();
        return;
    }

    setIsSaving(true);
    try {
        const updates: any = {};
        if (merchant) updates.merchant = merchant;
        if (normalizedMerchant) updates.normalized_merchant = normalizedMerchant;
        if (pfcPrimary) updates.pfc_primary = pfcPrimary;
        if (pfcDetailed) updates.pfc_detailed = pfcDetailed;

        await onSave(updates);
        onClose();
    } catch (error) {
        // Parent handles error toast usually
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg="bg.primary" color="text.primary" borderColor="xacto.borderGray" borderWidth="1px">
        <ModalHeader>Bulk Edit Transactions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Box bg="orange.900" p={4} borderRadius="md" borderLeftWidth="4px" borderLeftColor="orange.400">
                <Text fontSize="sm" color="orange.100">
                    You are editing <Badge colorScheme="orange" fontSize="0.9em" variant="solid">{selectedCount}</Badge> transactions.
                    Only fields you modify will be updated.
                </Text>
            </Box>

            <FormControl>
              <FormLabel color="text.secondary" fontSize="sm">Merchant Name (Optional)</FormLabel>
              <Input
                placeholder="Leave empty to keep original"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                bg="bg.surface"
                borderColor="xacto.borderGray"
                _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="text.secondary" fontSize="sm">Vendor (Normalized Merchant) (Optional)</FormLabel>
              <Input
                placeholder="Leave empty to keep original"
                value={normalizedMerchant}
                onChange={(e) => setNormalizedMerchant(e.target.value)}
                bg="bg.surface"
                borderColor="xacto.borderGray"
                _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
              />
            </FormControl>

            <Divider borderColor="gray.700" />

            <CategorySelector
                initialPrimary=""
                initialDetailed=""
                availableCategories={availableCategories}
                onChange={(p, d) => {
                    setPfcPrimary(p);
                    setPfcDetailed(d);
                }}
            />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
            Apply Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
